import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {Hint} from '@/components/ui/hint'
import {
  IDENTITY_VALIDATORS,
  PersonaBasicFields,
  clearChangedIdentityErrors,
  validateBasicIdentity,
  type PersonaIdentity,
} from '@/components/persona-identity-fields'
import {openFormaPdf, useAgregarPersona, type BateriaForma} from '@/hooks/bateria/use-bateria'
import {useTags} from '@/hooks/empresa/organizacion/use-tags'
import {translateApiError} from '@/lib/errors'

const EMPTY: PersonaIdentity = {name: '', last_name: '', cedula: '', tipo_documento: 'CC', email: '', phone: ''}
const FORMAS: BateriaForma[] = ['A', 'B']

// Se piden acá y no con un 409 posterior: completar exige un `employee_id` que aún no existe.
type Minimos = {area: string; cargo: string; sede: string}
const EMPTY_MINIMOS: Minimos = {area: '', cargo: '', sede: ''}
type MinimosErrors = Partial<Record<keyof Minimos, string>>

function validateMinimos(minimos: Minimos): MinimosErrors {
  const errors: MinimosErrors = {}
  if (!minimos.area.trim()) errors.area = 'admin.bateria.agregarPersona.areaRequired'
  if (!minimos.cargo.trim()) errors.cargo = 'admin.bateria.agregarPersona.cargoRequired'
  if (!minimos.sede.trim()) errors.sede = 'admin.bateria.agregarPersona.sedeRequired'
  return errors
}

/** Sin preselección: el cuadernillo se imprime según el cargo y adivinarlo marcaría algo que nadie confirmó. */
function validateForma(forma: BateriaForma | null): string | null {
  return forma ? null : 'admin.bateria.agregarPersona.formaRequired'
}

/**
 * Entra por cédula y no por nómina: en un ad-hoc la persona todavía no existe en Kairos.
 * Sin correo ni teléfono: no tiene login, recibe el cuadernillo en papel.
 */
export function AgregarPersonaDialog({
  businessId,
  evaluacionId,
  open,
  onOpenChange,
}: {
  businessId: string
  evaluacionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const agregar = useAgregarPersona(businessId, evaluacionId)
  const {data: areas} = useTags(businessId, 'areas', open)
  const {data: positions} = useTags(businessId, 'positions', open)
  const {data: sedes} = useTags(businessId, 'sedes', open)
  const [identity, setIdentity] = useState<PersonaIdentity>(EMPTY)
  const [minimos, setMinimos] = useState<Minimos>(EMPTY_MINIMOS)
  const [forma, setForma] = useState<BateriaForma | null>(null)
  const [errors, setErrors] = useState<Partial<Record<keyof PersonaIdentity, string>>>({})
  const [minimosErrors, setMinimosErrors] = useState<MinimosErrors>({})
  const [formaError, setFormaError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creado, setCreado] = useState<{id: string; nombre: string} | null>(null)
  const [printError, setPrintError] = useState<string | null>(null)

  function close() {
    setIdentity(EMPTY)
    setMinimos(EMPTY_MINIMOS)
    setForma(null)
    setErrors({})
    setMinimosErrors({})
    setFormaError(null)
    setError(null)
    setCreado(null)
    setPrintError(null)
    agregar.reset()
    onOpenChange(false)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const found = validateBasicIdentity(identity)
    const minimosFound = validateMinimos(minimos)
    const formaProblem = validateForma(forma)
    setErrors(found)
    setMinimosErrors(minimosFound)
    setFormaError(formaProblem)
    if (Object.keys(found).length > 0 || Object.keys(minimosFound).length > 0 || !forma) return
    agregar.mutate(
      {
        documento: identity.cedula.trim(),
        tipo_documento: identity.tipo_documento,
        nombres: identity.name.trim(),
        apellidos: identity.last_name.trim(),
        area: minimos.area.trim(),
        cargo: minimos.cargo.trim(),
        sede: minimos.sede.trim(),
        forma,
      },
      {
        onSuccess: (nuevo) => setCreado({id: nuevo.id, nombre: nuevo.employee_name}),
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  async function imprimir() {
    if (!creado) return
    setPrintError(null)
    try {
      await openFormaPdf(businessId, creado.id)
    } catch (err) {
      setPrintError(translateApiError(err))
    }
  }

  if (creado) {
    return (
      <Dialog open={open} onOpenChange={(next) => !next && close()}>
        <DialogContent className="max-w-lg" data-testid="agregar-persona-dialog">
          <DialogHeader>
            <DialogTitle>{t('admin.bateria.agregarPersona.exitoTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              {t('admin.bateria.agregarPersona.exito', {nombre: creado.nombre})}
            </p>
            {printError && <p className="text-xs text-destructive">{printError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close}>
                {t('common.close')}
              </Button>
              <Button data-testid="agregar-persona-imprimir" onClick={imprimir}>
                {t('admin.bateria.agregarPersona.imprimirSuCuadernillo')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="agregar-persona-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.agregarPersona.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">{t('admin.bateria.agregarPersona.intro')}</p>
          <PersonaBasicFields
            value={identity}
            onChange={(next) => {
              setErrors((prev) => clearChangedIdentityErrors(prev, next, identity))
              setIdentity(next)
            }}
            tKey="admin.bateria.agregarPersona"
            testPrefix="agregar-persona"
            errors={errors}
            onBlurField={(field) =>
              setErrors((prev) => ({
                ...prev,
                [field]: IDENTITY_VALIDATORS[field](identity[field], identity) ?? undefined,
              }))
            }
          />
          <CatalogOtherField
            id="agregar-persona-area"
            label={t('tags.kind.areas')}
            options={(areas?.items ?? []).map((a) => a.name)}
            value={minimos.area}
            onChange={(value) => {
              setMinimos((prev) => ({...prev, area: value}))
              setMinimosErrors((prev) => ({...prev, area: undefined}))
            }}
            onCommit={(value) => setMinimos((prev) => ({...prev, area: value}))}
          />
          <FieldError error={minimosErrors.area ? t(minimosErrors.area) : undefined} />
          <CatalogOtherField
            id="agregar-persona-cargo"
            label={t('tags.kind.positions')}
            options={(positions?.items ?? []).map((p) => p.name)}
            value={minimos.cargo}
            onChange={(value) => {
              setMinimos((prev) => ({...prev, cargo: value}))
              setMinimosErrors((prev) => ({...prev, cargo: undefined}))
            }}
            onCommit={(value) => setMinimos((prev) => ({...prev, cargo: value}))}
          />
          <FieldError error={minimosErrors.cargo ? t(minimosErrors.cargo) : undefined} />
          <CatalogOtherField
            id="agregar-persona-sede"
            label={t('ficha.sede')}
            options={(sedes?.items ?? []).map((s) => s.name)}
            value={minimos.sede}
            onChange={(value) => {
              setMinimos((prev) => ({...prev, sede: value}))
              setMinimosErrors((prev) => ({...prev, sede: undefined}))
            }}
            onCommit={(value) => setMinimos((prev) => ({...prev, sede: value}))}
          />
          <FieldError error={minimosErrors.sede ? t(minimosErrors.sede) : undefined} />
          <div className="space-y-2">
            <Label>{t('admin.bateria.agregarPersona.forma')}</Label>
            <div className="flex flex-wrap gap-4">
              {FORMAS.map((value) => (
                <label key={value} className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="radio"
                    name="agregar-persona-forma"
                    checked={forma === value}
                    onChange={() => {
                      setForma(value)
                      setFormaError(null)
                    }}
                    data-testid={`agregar-persona-forma-${value}`}
                    className="accent-primary"
                  />
                  {t('admin.bateria.agregarPersona.formaOption', {forma: value})}
                </label>
              ))}
            </div>
            <Hint>{t('admin.bateria.agregarPersona.formaHelp')}</Hint>
            <FieldError error={formaError ?? undefined} />
          </div>
          <FormDialogFooter
            error={error}
            pending={agregar.isPending}
            submitLabel={t('admin.bateria.agregarPersona.submit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
